import React, {
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { SelectReaction } from 'components/pharmacyMedications/Allergies';
import MuiAutoComplete from 'components/common/autoComplete';
import useMuiAutoCompleteCreatable from 'hooks/useMuiAutoCompleteCreatable';
import styles from 'components/common/common.module.scss';
import Label from 'components/common/label';
import { removeItemFromList } from 'components/medicalHistory/helpers';
import clsx from 'clsx';
import AddItemButton from 'components/common/addItemButton';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { NullableCommunique } from 'types/types';
import useDelay from 'hooks/useDelay';
import { ERxDrugAllergySearchResultDTO } from 'artifacts/dtos';
import { useERxDrugAllergySearch } from 'hooks/useApi';

type OptionType = {
  value: string;
  label: string;
  reaction?: string;
  readOnly?: boolean;
};

type Props = {
  option: OptionType[];
  value: OptionType[];
  id: string;
  placeholder: string;
  reaction: SelectReaction;
  allergyReactionSettingsOptions: string[];
  onChange?: (item: OptionType[]) => void;
  addButtonLabel?: string;
  disabled?: boolean;
  disabledTooltip?: string;
};

function SearchableTextBoxAllergies({
  option,
  placeholder,
  onChange,
  value,
  reaction,
  allergyReactionSettingsOptions,
  addButtonLabel,
  disabled,
  disabledTooltip,
}: Props) {
  const communiqueData = useSelector(
    (state: RootState) => state.communique,
  ) as NullableCommunique;
  const [inputValue, setInputValue] = React.useState<OptionType[]>(value || []);
  const [inputText, setInputText] = useState('');
  const [data, setData] = useState<ERxDrugAllergySearchResultDTO[]>([]);
  const delay = useDelay(inputText, 500);
  const {
    mutateAsync: drugAllergySearch,
    isPending: isLoading,
    isError,
    reset,
  } = useERxDrugAllergySearch();
  const filterOptions = useMuiAutoCompleteCreatable();
  const [hideSearchBox, setHideSearchBox] = useState(false);

  useEffect(() => {
    if ((delay ?? '').length > 2) {
      drugAllergySearch({
        search: delay,
        organizationId: communiqueData?.organizationId ?? undefined,
      })
        .then((items) => { setData(items); })
        .catch(() => { setData([]); });
    }
  }, [communiqueData?.organizationId, delay, drugAllergySearch]);

  useEffect(() => {
    if (value?.length) {
      setHideSearchBox(true);
    }
  }, [value]);

  const isValidInputLength = useMemo(() => (inputText.length > 2), [inputText]);

  const mappingOptions = useMemo(
    () => data?.map((item) => ({
      ...item,
      value: item.displayName ?? '',
      label: item.displayName ?? '',
    })),
    [data],
  );

  const removeItem = (item: OptionType) => {
    setInputValue((prev) => {
      const { items, isEmpty } = removeItemFromList(prev, item);
      setHideSearchBox(!isEmpty);
      return items;
    });
  };

  useEffect(() => {
    if (onChange) onChange(inputValue);
  }, [inputValue]);

  useEffect(() => {
    if (value) setInputValue(value);
  }, [value]);

  const onChangeReaction = (
    moduleType: SelectReaction,
    reactionName: string,
    value: string,
  ) => {
    setInputValue((old: OptionType[]) => old.filter((e: OptionType) => {
      if (e.value === reactionName) {
        e.reaction = value;
        return e;
      } return e;
    }));
  };

  const onInputChange = (event: SyntheticEvent, newInputValue: string) => {
    setInputText(newInputValue);
    reset?.();
    setData([]);
  };

  // eslint-disable-next-line no-nested-ternary
  const genNoOptionsText = useMemo(() => (isLoading
    // eslint-disable-next-line no-nested-ternary
    ? 'Loading....' : isError
      ? 'Something went wrong' : isValidInputLength
        ? 'No Options' : 'Please type a Drug Allergy, min characters 3'), [isLoading, isError, isValidInputLength]);

  const addButtonClass = clsx((inputValue.length === 0 || hideSearchBox)
    ? 'addButton'
    : 'addButtonDisable');
  return (
    <Stack>
      {inputValue.map((item, index) => {
        const drugAllergicReaction = inputValue.find(
          (e: OptionType) => e.value === item.value,
        );

        const reactionClass = clsx(
          styles.selectAllergies,
          {
            [styles.selectAllergiesSelectError]: !drugAllergicReaction?.reaction
            && SelectReaction.nonDrugReaction === reaction,
          },
        );
        return (
          <Box key={item.label} className={clsx('mt-4 pt-2', { 'border-t': index > 0 })}>
            <Label>
              {
                SelectReaction.drugReaction === reaction
                  ? 'Which medications are you allergic to?'
                  : 'Which other things are you allergic to?'
              }
            </Label>
            <Box
              component="div"
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginTop: 3,
                marginBottom: 1.5,
                justifyContent: 'space-between',
              }}
              className={clsx(
                ' flex my-2 justify-between',
                { 'autocomplete-selected-item': !item?.readOnly },
                { 'autocomplete-readOnly-item': item?.readOnly },
              )}
            >
              <p>{item.value}</p>
              {!item?.readOnly && (
                <ClearIcon
                  onClick={() => { removeItem(item); }}
                  fontSize="small"
                />
              )}
            </Box>

            <Label>
              {
                SelectReaction.drugReaction === reaction
                  ? 'What happens when you take this drug?'
                  : 'What type of allergic reaction?'
              }
            </Label>
            <p className={styles.separator} />
            {
              item.readOnly
                ? (<p className="autocomplete-readOnly-item">{item.reaction}</p>)
                : (
                  <select
                    value={drugAllergicReaction?.reaction || undefined}
                    onChange={(event) => onChangeReaction(reaction, item.value, event.target.value)}
                    style={{ width: '100%' }}
                    disabled={item?.readOnly}
                    className={reactionClass}
                  >
                    {allergyReactionSettingsOptions.map((name) => (
                      <option
                        key={name}
                        value={name}
                        disabled={name === 'Select Reaction Type'}
                        selected={name === 'Select Reaction Type'}
                      >
                        {name}
                      </option>
                    ))}
                  </select>
                )
            }
          </Box>
        );
      })}

      <Box mt={1} />

      {SelectReaction.drugReaction === reaction
        ? (
          <MuiAutoComplete
            options={mappingOptions}
            value={inputValue}
            placeholder={placeholder}
            onChange={(event, newInputValue) => {
              if (inputValue.length < newInputValue.length) {
                setInputValue(newInputValue);
              }
            }}
            onInputChange={onInputChange}
            noOptionsText={genNoOptionsText}
            hide={hideSearchBox}
            disabled={disabled}
            disabledTooltip={disabled ? disabledTooltip : undefined}
          />
        )
        : (
          <MuiAutoComplete
            options={option}
            value={inputValue}
            placeholder={placeholder}
            onChange={(event, newInputValue) => {
              if (inputValue.length < newInputValue.length) {
                setInputValue(newInputValue);
              }
            }}
            filterOptions={filterOptions}
            hide={hideSearchBox}
          />
        )}

      <p className={styles.separator} />
      <AddItemButton
        className={addButtonClass}
        label={addButtonLabel ?? 'Add new'}
        onClick={() => setHideSearchBox(false)}
        disabled={(inputValue.length === 0 && !hideSearchBox) || disabled}
      />
    </Stack>
  );
}

export default SearchableTextBoxAllergies;
